@file:Suppress("FunctionName")

import androidx.compose.runtime.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.launch
import kotlinx.browser.window
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.*

object AdminStyles : StyleSheet() {
    val sidebar by style {
        width(240.px); backgroundColor(Color.white); display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column); property("border-right", "1px solid #d1d9e0"); padding(16.px)
        property("transition", "width 0.3s ease, padding 0.3s ease")
        
        media("(max-width: 1024px)") {
            self style {
                width(72.px)
                padding(16.px, 8.px)
            }
        }
    }
    
    val sidebarHeader by style {
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px); marginBottom(24.px)
        media("(max-width: 1024px)") {
            self style {
                justifyContent(JustifyContent.Center)
                gap(0.px)
            }
        }
    }

    val sidebarItem by style {
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px)
        padding(10.px, 16.px)
        borderRadius(8.px)
        marginBottom(4.px)
        cursor("pointer")
        property("transition", "all 0.2s")
        
        media("(max-width: 1024px)") {
            self style {
                justifyContent(JustifyContent.Center)
                padding(10.px, 0.px)
                gap(0.px)
            }
        }
    }
    
    val sidebarLabel by style {
        media("(max-width: 1024px)") {
            self style {
                display(DisplayStyle.None)
            }
        }
    }
    
    val tableWrapper by style {
        backgroundColor(Color.white); borderRadius(12.px); property("border", "1px solid #e2e8f0")
        property("box-shadow", "0 4px 6px -1px rgba(0,0,0,0.1)"); overflowX("auto")
    }
}

// Session state for credentials
private var credentialsState by mutableStateOf<BasicAuthCredentials?>(null)

private val adminClient = HttpClient {
    install(ContentNegotiation) {
        json(Json {
            ignoreUnknownKeys = true
            isLenient = true
        })
    }
    install(Auth) {
        basic {
            credentials { credentialsState }
            sendWithoutRequest { request ->
                request.url.pathSegments.contains("admin")
            }
        }
    }
}

@Composable
fun AdminDashboard() {
    Style(AdminStyles)
    // Show login screen if not authenticated
    if (credentialsState == null) {
        AdminLoginScreen { user, pass ->
            credentialsState = BasicAuthCredentials(username = user, password = pass)
        }
        return
    }

    var currentTab by remember { mutableStateOf(LeadTab.Candidate) }
    var employeeLeads by remember { mutableStateOf<List<CandidateLead>>(emptyList()) }
    var recruiterLeads by remember { mutableStateOf<List<RecruiterLead>>(emptyList()) }
    var selectedLeadId by remember { mutableStateOf<Int?>(null) }
    
    // Pagination & Filter State
    var currentPage by remember { mutableStateOf(1) }
    var totalPages by remember { mutableStateOf(1) }
    var searchQuery by remember { mutableStateOf("") }
    var statusFilter by remember { mutableStateOf("") }
    var startDate by remember { mutableStateOf("") }
    var endDate by remember { mutableStateOf("") }

    val scope = rememberCoroutineScope()

    fun refreshData() {
        scope.launch {
            try {
                println("Fetching leads for $currentTab, page $currentPage")
                if (currentTab == LeadTab.Candidate) {
                    val response: PaginatedResponse<CandidateLead> = adminClient.get("/api/admin/employees") {
                        parameter("page", currentPage)
                        parameter("pageSize", 30)
                        parameter("search", searchQuery)
                        if (statusFilter.isNotEmpty()) parameter("status", statusFilter)
                    }.body()
                    employeeLeads = response.items
                    totalPages = response.totalPages
                    currentPage = response.currentPage
                    println("Fetched ${employeeLeads.size} employee leads. Total pages: $totalPages")
                } else {
                    val response: PaginatedResponse<RecruiterLead> = adminClient.get("/api/admin/recruiters") {
                        parameter("page", currentPage)
                        parameter("pageSize", 30)
                        parameter("search", searchQuery)
                        if (statusFilter.isNotEmpty()) parameter("status", statusFilter)
                    }.body()
                    recruiterLeads = response.items
                    totalPages = response.totalPages
                    currentPage = response.currentPage
                    println("Fetched ${recruiterLeads.size} recruiter leads. Total pages: $totalPages")
                }
            } catch (e: Exception) {
                if (e is ClientRequestException && e.response.status == HttpStatusCode.Unauthorized) {
                    credentialsState = null
                }
                println("Error fetching leads: ${e.message}")
                e.printStackTrace()
            }
        }
    }

    LaunchedEffect(currentTab, currentPage, statusFilter) {
        refreshData()
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            minHeight(100.vh)
            backgroundColor(Color("#f0f4f8"))
            fontFamily("Inter", "sans-serif")
        }
    }) {
        // Sidebar
        Div({
            classes(AdminStyles.sidebar)
        }) {
            Div({ classes(AdminStyles.sidebarHeader) }) {
                Img(src = "logo.png") { style { width(28.px); height(28.px); borderRadius(6.px); property("flex-shrink", "0") } }
                H2({ 
                    classes(AdminStyles.sidebarLabel)
                    style { margin(0.px); color(Color("#1a365d")); fontSize(18.px); property("white-space", "nowrap") } 
                }) { Text("Admin Panel") }
            }
            SidebarItem("Employee Leads", icon = "fa-users", active = currentTab == LeadTab.Candidate) { 
                currentTab = LeadTab.Candidate; currentPage = 1 
            }
            SidebarItem("Recruiter Leads", icon = "fa-briefcase", active = currentTab == LeadTab.Recruiter) {
                currentTab = LeadTab.Recruiter; currentPage = 1 
            }
            Div({ style { property("margin-top", "auto") } }) {
                SidebarItem("Logout", icon = "fa-sign-out-alt", active = false) { credentialsState = null }
            }
        }

        // Main Content
        Div({
            style { flex(1); padding(24.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(16.px) }
        }) {
            H1({ style { color(Color("#2d3748")); fontSize(24.px); margin(0.px); marginBottom(8.px) } }) {
                Text(if (currentTab == LeadTab.Candidate) "Employee Registration Leads" else "Recruiter Leads")
            }

            // Controls: Search, Filter, Export
            Div({
                style {
                    display(DisplayStyle.Flex); gap(12.px); alignItems(AlignItems.Center); flexWrap(FlexWrap.Wrap)
                    backgroundColor(Color.white); padding(12.px, 16.px); borderRadius(12.px); border(1.px, LineStyle.Solid, Color("#e2e8f0"))
                }
            }) {
                Input(InputType.Text) {
                    classes("search-input")
                    style {
                        padding(8.px, 12.px); borderRadius(6.px); border(1.px, LineStyle.Solid, Color("#d1d9e0"))
                        minWidth(200.px); property("outline", "none")
                    }
                    attr("placeholder", "Search...")
                    value(searchQuery)
                    onInput { searchQuery = it.value }
                    onKeyDown { if (it.key == "Enter") { currentPage = 1; refreshData() } }
                }

                Button({
                    style {
                        padding(8.px, 14.px); borderRadius(6.px); border(0.px); backgroundColor(Color("#4a5568"))
                        color(Color.white); fontWeight("600"); cursor("pointer")
                    }
                    onClick { currentPage = 1; refreshData() }
                }) { Text("Search") }

                Select({
                    style { padding(8.px); borderRadius(6.px); border(1.px, LineStyle.Solid, Color("#d1d9e0")) }
                    onInput { statusFilter = it.value ?: ""; currentPage = 1 }
                }) {
                    Option("") { Text("All Status") }
                    LeadStatus.entries.forEach { Option(it.name) { Text(it.name) } }
                }

                Div({ style { height(24.px); width(1.px); backgroundColor(Color("#e2e8f0")); margin(0.px, 4.px) } })

                Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                    Label { Text("From: "); Input(InputType.Date) { 
                        style { padding(6.px); borderRadius(4.px); border(1.px, LineStyle.Solid, Color("#d1d9e0")) }
                        value(startDate); onInput { startDate = it.value } 
                    } }
                    Label { Text("To: "); Input(InputType.Date) { 
                        style { padding(6.px); borderRadius(4.px); border(1.px, LineStyle.Solid, Color("#d1d9e0")) }
                        value(endDate); onInput { endDate = it.value } 
                    } }
                }

                Button({
                    style {
                        property("margin-left", "auto"); padding(8.px, 16.px); borderRadius(6.px); border(0.px); backgroundColor(Color("#2b6cb0"))
                        color(Color.white); fontWeight("600"); cursor("pointer")
                    }
                    onClick {
                        val endpoint = if (currentTab == LeadTab.Candidate) "employees" else "recruiters"
                        window.open("/api/admin/export/$endpoint?start=$startDate&end=$endDate", "_blank")
                    }
                }) { Text("Export CSV") }
            }

            // Table Content
            Div({ classes(AdminStyles.tableWrapper) }) {
                Table({ style { width(100.percent); property("border-collapse", "collapse") } }) {
                    Thead {
                        Tr {
                            if (currentTab == LeadTab.Candidate) {
                                Th("Name")
                                Th("Experience")
                                Th("Industry")
                                Th("Details")
                                Th("Status")
                                Th("Actions")
                            } else {
                                Th("Company")
                                Th("Employer")
                                Th("Needs")
                                Th("Details")
                                Th("Status")
                                Th("Actions")
                            }
                        }
                    }
                    Tbody {
                        if (currentTab == LeadTab.Candidate) {
                            if (employeeLeads.isEmpty()) {
                                Tr { Td({ attr("colspan", "6"); style { textAlign("center"); padding(40.px) } }) { Text("No candidate leads found.") } }
                            }
                            employeeLeads.forEach { lead ->
                                EmployeeRow(lead, 
                                    onDetails = { selectedLeadId = it }, 
                                    onUpdateStatus = { status ->
                                        scope.launch { updateStatus(lead.id!!, status, "employee"); refreshData() }
                                    },
                                    onDelete = { id ->
                                        scope.launch { deleteLead(id, "employee"); refreshData() }
                                    }
                                )
                            }
                        } else {
                            if (recruiterLeads.isEmpty()) {
                                Tr { Td({ attr("colspan", "6"); style { textAlign("center"); padding(40.px) } }) { Text("No recruiter leads found.") } }
                            }
                            recruiterLeads.forEach { lead ->
                                RecruiterRow(lead, 
                                    onDetails = { selectedLeadId = it }, 
                                    onUpdateStatus = { status ->
                                        scope.launch { updateStatus(lead.id!!, status, "recruiter"); refreshData() }
                                    },
                                    onDelete = { id ->
                                        scope.launch { deleteLead(id, "recruiter"); refreshData() }
                                    }
                                )
                            }
                        }
                    }
                }
                
                // Pagination Controls
                Div({
                    style {
                        padding(16.px); display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); gap(12.px)
                        property("border-top", "1px solid #e2e8f0"); backgroundColor(Color("#f8fafc"))
                    }
                }) {
                    Button({
                        if (currentPage <= 1) disabled()
                        onClick { if (currentPage > 1) currentPage-- }
                        style { padding(8.px, 16.px); borderRadius(6.px); border(1.px, LineStyle.Solid, Color("#d1d9e0")) }
                    }) { Text("Previous") }
                    
                    Span({ style { alignSelf(AlignSelf.Center); fontWeight("600") } }) {
                        Text("Page $currentPage of $totalPages")
                    }

                    Button({
                        if (currentPage >= totalPages) disabled()
                        onClick { if (currentPage < totalPages) currentPage++ }
                        style { padding(8.px, 16.px); borderRadius(6.px); border(1.px, LineStyle.Solid, Color("#d1d9e0")) }
                    }) { Text("Next") }
                }
            }
        }
    }

    selectedLeadId?.let { id ->
        val candidate = if (currentTab == LeadTab.Candidate) employeeLeads.find { it.id == id } else null
        val recruiter = if (currentTab == LeadTab.Recruiter) recruiterLeads.find { it.id == id } else null
        DetailsModal(candidate, recruiter, onClose = { selectedLeadId = null })
    }
}

@Composable
fun AdminLoginScreen(onLogin: (String, String) -> Unit) {
    var user by remember { mutableStateOf("") }
    var pass by remember { mutableStateOf("") }

    Div({
        style {
            display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center)
            height(100.vh); backgroundColor(Color("#f0f4f8"))
        }
    }) {
        Div({
            style {
                backgroundColor(Color.white); padding(40.px); borderRadius(20.px)
                property("box-shadow", "0 10px 25px rgba(0,0,0,0.1)"); width(350.px)
            }
        }) {
            H2({ style { property("text-align", "center"); marginBottom(24.px) } }) { Text("Admin Login") }
            
            Label {
                Text("Username")
                Input(InputType.Text) {
                    style {
                        width(100.percent); padding(12.px); borderRadius(8.px); border(1.px, LineStyle.Solid, Color("#d1d9e0"))
                        marginTop(8.px); marginBottom(16.px); property("outline", "none")
                    }
                    value(user); onInput { user = it.value }
                }
            }

            Label {
                Text("Password")
                Input(InputType.Password) {
                    style {
                        width(100.percent); padding(12.px); borderRadius(8.px); border(1.px, LineStyle.Solid, Color("#d1d9e0"))
                        marginTop(8.px); marginBottom(24.px); property("outline", "none")
                    }
                    value(pass); onInput { pass = it.value }
                }
            }

            Button({
                style {
                    width(100.percent); padding(14.px); borderRadius(8.px); border(0.px)
                    backgroundColor(Color("#6750a4")); color(Color.white); fontWeight("600"); cursor("pointer")
                }
                onClick { onLogin(user, pass) }
            }) { Text("Login") }
        }
    }
}

@Composable
fun DetailsModal(candidate: CandidateLead?, recruiter: RecruiterLead?, onClose: () -> Unit) {
    Div({
        style {
            position(Position.Fixed); top(0.px); property("left", "0"); width(100.percent); height(100.percent)
            backgroundColor(rgba(0, 0, 0, 0.6)); display(DisplayStyle.Flex); justifyContent(JustifyContent.Center)
            property("align-items", "center"); property("z-index", "1001")
        }
        onClick { onClose() }
    }) {
        Div({
            style {
                backgroundColor(Color.white); borderRadius(20.px); padding(32.px); maxWidth(600.px); width(90.percent)
                property("max-height", "80vh"); property("overflow-y", "auto")
            }
            onClick { it.stopPropagation() }
        }) {
            H2 { Text(if (candidate != null) "Candidate Details" else "Recruiter Details") }
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px); marginTop(20.px) } }) {
                candidate?.let { c ->
                    val fullName = if (c.middleName.isNotBlank()) "${c.firstName} ${c.middleName} ${c.lastName}" else "${c.firstName} ${c.lastName}"
                    DetailItem("Full Name", fullName)
                    DetailItem("Email", c.email)
                    DetailItem("Temporary Address", c.temporaryAddress)
                    DetailItem("Permanent Address", c.permanentAddress)
                    DetailItem("Phone", c.phone)
                    DetailItem("Alternate Phone", c.alternateNo)
                    DetailItem("Total Experience", c.totalExperience)
                    DetailItem("Relevant Industry Experience", c.fieldExperience)
                    DetailItem("Additional Experience (If Any)", c.otherExperience)
                    DetailItem("Current CTC (Annual)", c.currentCtc)
                    DetailItem("Expected CTC (Annual)", c.expectedCtc)
                    DetailItem("Current Industry", c.currentIndustry)
                    if (c.currentIndustry == "Other") {
                        DetailItem("Specified Current Industry", c.otherCurrentIndustry)
                    }
                    DetailItem("Expected Industry", c.expectedIndustry)
                    if (c.expectedIndustry == "Other") {
                        DetailItem("Specified Expected Industry", c.otherExpectedIndustry)
                    }
                    DetailItem("Notice Period", c.noticePeriod)
                    DetailItem("Preferred Job Type", c.preferredJobType)
                    DetailItem("How did you hear about us? (Source)", c.referenceLead)
                    DetailItem("Key Skills", c.keySkills)
                    DetailItem("Current Location", c.currentLocation)
                    DetailItem("Preferred Job Location", c.expectedLocations)
                    DetailItem("Email Alerts", if (c.emailAlerts) "Yes" else "No")
                    DetailItem("Resume", c.resumeName)
                    if (c.resumeName.isNotEmpty()) {
                        A(href = "/uploads/resumes/${c.resumeName}", attrs = {
                            attr("target", "_blank")
                            style {
                                color(Color("#3182ce")); textDecoration("underline")
                                fontSize(14.px); marginTop(4.px)
                            }
                        }) { Text("Download/View Resume") }
                    }
                    DetailItem("Payment Commitment", c.paymentCommitment)
                    DetailItem("Registration Fee", "₹${c.registrationFee}")
                }
                recruiter?.let { r ->
                    H4({ style { color(Color("#0bade3")); margin(12.px, 0.px, 4.px, 0.px) } }) { Text("Section 1: Employer Details") }
                    DetailItem("Company Name", r.companyName)
                    DetailItem("Employer Full Name", r.employerName)
                    DetailItem("Designation", r.designation)
                    DetailItem("Department", r.employerDepartment)
                    DetailItem("Total Vacancies", r.totalVacancies)
                    DetailItem("Email", r.companyEmail)
                    DetailItem("Contact", r.contactNumber)
                    DetailItem("Address", r.companyAddress)
                    DetailItem("Website", r.companyWebsite)
                    DetailItem("GSTIN", r.gstnNo)
                    
                    H4({ style { color(Color("#0bade3")); margin(12.px, 0.px, 4.px, 0.px) } }) { Text("Section 2: Recruitment Details") }
                    DetailItem("Position Required", r.requiredPosition)
                    DetailItem("Department", r.recruitmentDepartment)
                    if (r.recruitmentDepartment == "Other") {
                        DetailItem("Specified Department", r.otherRecruitmentDepartment)
                    }
                    DetailItem("Dept Vacancies", r.departmentVacancies)
                    DetailItem("Offered CTC", r.offeredCtc)
                    DetailItem("Experience Required", r.experienceRequired)
                    DetailItem("Qualification", r.qualification)
                    DetailItem("Job Type", r.jobType)
                    if (r.jobType == "Other") {
                        DetailItem("Specified Job Type", r.otherJobType)
                    }
                    DetailItem("Gender Preference", r.genderPreference)
                    DetailItem("Joining Timeline", r.joiningTimeline)
                    DetailItem("Interview Mode", r.interviewMode)
                    DetailItem("Skills Required", r.skillsRequired)
                    DetailItem("Special Note", r.specialNote)
                    DetailItem("Hiring Description", r.hiringDescription)
                }
            }
            Button({
                style {
                    marginTop(32.px); padding(12.px, 24.px); borderRadius(8.px); border(0.px)
                    backgroundColor(Color("#6750a4")); color(Color.white); cursor("pointer")
                }
                onClick { onClose() }
            }) { Text("Close") }
        }
    }
}

@Composable
fun DetailItem(label: String, value: String) = Div {
    Span({ style { fontWeight("700"); color(Color("#4a5568")) } }) { Text("$label: ") }
    Span { Text(value) }
}

@Composable
fun SidebarItem(label: String, icon: String? = null, active: Boolean, onClick: () -> Unit) {
    Div({
        onClick { onClick() }
        classes(AdminStyles.sidebarItem)
        style {
            fontWeight(if (active) "600" else "400")
            backgroundColor(if (active) Color("#e2e8f0") else Color.transparent)
            color(if (active) Color("#2b6cb0") else Color("#4a5568"))
            if (label.isEmpty() && icon != null) {
                width(40.px); height(40.px); padding(0.px)
            }
        }
        if (label.isEmpty() && icon != null) attr("title", "Logout")
        else if (label.isNotEmpty()) attr("title", label)
    }) {
        icon?.let { I({ classes("fas", it) }) }
        if (label.isNotEmpty()) Span({ classes(AdminStyles.sidebarLabel) }) { Text(label) }
    }
}

@Composable
private fun Th(text: String) {
    org.jetbrains.compose.web.dom.Th({
        style {
            textAlign("left")
            padding(12.px, 16.px)
            backgroundColor(Color("#f8fafc"))
            color(Color("#64748b"))
            fontSize(13.px)
            fontWeight("600")
            property("border-bottom", "2px solid #e2e8f0")
        }
    }) { Text(text) }
}

@Composable
private fun Td(content: @Composable () -> Unit) {
    org.jetbrains.compose.web.dom.Td({
        style {
            padding(10.px, 16.px)
            property("border-bottom", "1px solid #f1f5f9")
            fontSize(14.px)
            color(Color("#334155"))
        }
    }) { content() }
}

@Composable
private fun Td(attrs: AttrBuilderContext<org.w3c.dom.HTMLTableCellElement>? = null, content: @Composable () -> Unit) {
    org.jetbrains.compose.web.dom.Td({
        attrs?.invoke(this)
        style {
            padding(10.px, 16.px)
            property("border-bottom", "1px solid #f1f5f9")
            fontSize(14.px)
            color(Color("#334155"))
        }
    }) { content() }
}

@Composable
fun EmployeeRow(lead: CandidateLead, onDetails: (Int) -> Unit, onUpdateStatus: (LeadStatus) -> Unit, onDelete: (Int) -> Unit) {
    Tr {
        Td { Text("${lead.firstName} ${lead.lastName}") }
        Td { Text(lead.totalExperience) }
        Td { Text(lead.currentIndustry) }
        Td {
            Button({
                style { color(Color("#3182ce")); border(0.px); background("transparent"); cursor("pointer"); textDecoration("underline") }
                onClick { onDetails(lead.id!!) }
            }) { Text("View Details") }
        }
        Td { StatusBadge(lead.status) }
        Td { ActionButtons(lead.phone, onUpdateStatus, onDelete = { onDelete(lead.id!!) }) }
    }
}

@Composable
fun RecruiterRow(lead: RecruiterLead, onDetails: (Int) -> Unit, onUpdateStatus: (LeadStatus) -> Unit, onDelete: (Int) -> Unit) {
    Tr {
        Td { Text(lead.companyName) }
        Td { Text(lead.employerName) }
        Td {
            Div({
                style {
                    maxWidth(200.px); property("text-overflow", "ellipsis"); whiteSpace("nowrap"); overflow("hidden")
                }
            }) { Text(lead.hiringDescription) }
        }
        Td {
            Button({
                style { color(Color("#3182ce")); border(0.px); background("transparent"); cursor("pointer"); textDecoration("underline") }
                onClick { onDetails(lead.id!!) }
            }) { Text("View Details") }
        }
        Td { StatusBadge(lead.status) }
        Td { ActionButtons(lead.contactNumber, onUpdateStatus, onDelete = { onDelete(lead.id!!) }) }
    }
}

@Composable
fun StatusBadge(status: LeadStatus) {
    val (bg, fg) = when (status) {
        LeadStatus.PENDING -> Color("#fef3c7") to Color("#92400e")
        LeadStatus.APPROVED -> Color("#dcfce7") to Color("#166534")
        LeadStatus.REJECTED -> Color("#fee2e2") to Color("#991b1b")
        LeadStatus.REVIEWED -> Color("#e0f2fe") to Color("#075985")
    }
    Span({
        style {
            padding(4.px, 12.px)
            borderRadius(99.px)
            fontSize(12.px)
            fontWeight("600")
            backgroundColor(bg)
            color(fg)
            property("text-transform", "uppercase")
        }
    }) { Text(status.name) }
}

@Composable
fun ActionButtons(phone: String, onUpdate: (LeadStatus) -> Unit, onDelete: () -> Unit) {
    Div({ style { display(DisplayStyle.Flex); gap(6.px); alignItems(AlignItems.Center) } }) {
        ActionButton("Approve", Color("#48bb78")) { onUpdate(LeadStatus.APPROVED) }
        ActionButton("Reject", Color("#f56565")) { onUpdate(LeadStatus.REJECTED) }
        ActionButton("Review", Color("#4299e1")) { onUpdate(LeadStatus.REVIEWED) }
        ActionButton("Delete", Color("#e53e3e")) {
            if (window.confirm("Are you sure you want to delete this record?")) {
                onDelete()
            }
        }

        val sanitizedPhone = phone.filter { it.isDigit() }.let {
            if (it.length == 10) "91$it" else it
        }
        A(href = "https://wa.me/$sanitizedPhone", attrs = {
            attr("target", "_blank")
            style {
                padding(6.px, 8.px); borderRadius(6.px); backgroundColor(Color("#25D366"))
                color(Color.white); fontSize(16.px); textDecoration("none")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            }
        }) { 
            I({ classes("fab", "fa-whatsapp") })
        }
    }
}

@Composable
fun ActionButton(label: String, color: CSSColorValue, onClick: () -> Unit) {
    Button({
        onClick { onClick() }
        style {
            padding(4.px, 8.px)
            borderRadius(4.px)
            border(0.px)
            backgroundColor(color)
            color(Color.white)
            fontSize(11.px)
            fontWeight("600")
            cursor("pointer")
            property("transition", "opacity 0.2s")
        }
    }) { Text(label) }
}

private suspend fun updateStatus(id: Int, status: LeadStatus, type: String) {
    try {
        adminClient.patch("/api/admin/leads/$id/status") {
            contentType(ContentType.Application.Json)
            setBody(mapOf("status" to status.name, "type" to type))
        }
    } catch (e: Exception) {
        println("Update failed: ${e.message}")
    }
}

private suspend fun deleteLead(id: Int, type: String) {
    try {
        adminClient.post("/api/admin/leads/$id/delete") {
            parameter("type", type)
        }
    } catch (e: Exception) {
        println("Delete failed: ${e.message}")
    }
}
